import { Link } from 'react-router-dom'
import Seo from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import { useLocale, localizedHref } from '../i18n/locale'

interface Guide { slug: string; name: string; blurb: string; category: string; live: boolean }
interface CalcResource { name: string; href: string; blurb: string }
interface Reference { title: string; items: string[] }

interface ResourcesContent {
  seoTitle: string
  seoDesc: string
  crumbHome: string
  crumbResources: string
  h1: string
  intro: string
  calcH2: string
  calcSub: string
  browseAll: string
  guidesH2: string
  guidesSub: string
  readGuide: string
  comingSoon: string
  refH2: string
  refSub: string
  ctaH2: string
  ctaLead: string
  ctaNote: string
  ctaGetInTouch: string
  guides: Guide[]
  calcResources: CalcResource[]
  reference: Reference[]
}

const EN: ResourcesContent = {
  seoTitle: 'Renovation Resources, Guides & Reference Data | RenoSheets',
  seoDesc:
    'Practical renovation guides, how-to checklists, and quick-reference data tables for paint coverage, tile waste, grout joints, and concrete quantities.',
  crumbHome: 'Home',
  crumbResources: 'Resources',
  h1: 'Resources',
  intro:
    'How-to guides, renovation checklists, and quick-reference tables to help you plan and execute your project.',
  calcH2: 'Live calculators',
  calcSub: 'Jump to a tool and get your materials list now.',
  browseAll: 'Browse all calculators →',
  guidesH2: 'Step-by-step guides',
  guidesSub: "In-depth how-tos linked to the calculators you'll need.",
  readGuide: 'Read guide →',
  comingSoon: 'Coming soon',
  refH2: 'Quick reference',
  refSub: 'Industry-standard figures used in our calculators.',
  ctaH2: 'More guides coming soon',
  ctaLead: "We're writing in-depth how-to guides for the most common renovation projects.",
  ctaNote: 'Questions or suggestions?',
  ctaGetInTouch: 'Get in touch',
  guides: [
    {
      slug: 'how-to-paint-a-room',
      name: 'How to Paint a Room',
      blurb: 'Prep, prime, cut-in, and roll — step by step, with a complete materials checklist.',
      category: 'Paint & Walls',
      live: true,
    },
    {
      slug: 'bathroom-renovation-checklist',
      name: 'Bathroom Renovation Checklist',
      blurb: 'Everything to plan, buy, and schedule for a full bathroom gut-and-replace.',
      category: 'Planning',
      live: true,
    },
    {
      slug: 'kitchen-remodel-planning-guide',
      name: 'Kitchen Remodel Planning Guide',
      blurb: 'The trade-by-trade order of work, what to line up, and where flooring and cabinets fit.',
      category: 'Planning',
      live: true,
    },
    {
      slug: 'tile-installation-guide',
      name: 'Tile Installation Guide',
      blurb: 'Substrate prep, layout lines, setting tile, grouting, and sealing — for floors and walls.',
      category: 'Flooring & Tile',
      live: true,
    },
    {
      slug: 'deck-building-materials-list',
      name: 'Deck Building Materials List',
      blurb: 'Footings, joists, decking boards, hardware — everything in one checklist.',
      category: 'Outdoor',
      live: true,
    },
    {
      slug: 'concrete-mixing-and-pouring',
      name: 'Concrete Mixing & Pouring',
      blurb: 'Mix ratios, curing times, and common mistakes for slabs, footings, and post holes.',
      category: 'Concrete',
      live: true,
    },
  ],
  calcResources: [
    { name: 'Paint Calculator', href: '/calculators/paint/', blurb: 'Gallons, primer, roller covers and tape for any room.' },
    { name: 'Tile Calculator', href: '/calculators/tile/', blurb: 'Tile count, thinset, grout, spacers and backer board.' },
    { name: 'Concrete Calculator', href: '/calculators/concrete/', blurb: 'Bags or yards for slabs, footings and post holes.' },
    { name: 'Mulch Calculator', href: '/calculators/mulch/', blurb: 'Bags vs. bulk cubic yards for any bed depth.' },
  ],
  reference: [
    {
      title: 'Paint coverage by surface type',
      items: [
        'Smooth drywall: 350–400 sq ft per gallon',
        'Textured/rough walls: 250–300 sq ft per gallon',
        'Bare wood (primer): 200–300 sq ft per gallon',
        'Masonry: 100–150 sq ft per gallon',
      ],
    },
    {
      title: 'Tile waste factors',
      items: [
        'Straight lay, square room: 10%',
        'Diagonal or 45° layout: 15–20%',
        'Complex pattern or many cuts: 20%+',
        'Always buy from the same dye lot',
      ],
    },
    {
      title: 'Grout joint guides',
      items: [
        '1/16 in: rectified large-format porcelain',
        '1/8 in: natural stone, polished tile',
        '3/16 in: standard ceramic (most common)',
        '1/4 in: handmade, rustic, or brick tile',
      ],
    },
    {
      title: 'Concrete bag coverage',
      items: [
        '40 lb bag: ~0.30 cu ft',
        '60 lb bag: ~0.45 cu ft',
        '80 lb bag: ~0.60 cu ft',
        '1 yard = 27 cu ft ≈ 45 × 80 lb bags',
      ],
    },
  ],
}

const FR: ResourcesContent = {
  seoTitle: 'Ressources de rénovation, guides et données de référence | RenoSheets',
  seoDesc:
    'Des guides pratiques de rénovation, des listes de vérification et des tableaux de référence rapide pour la couverture de peinture, la perte de carrelage, les joints de coulis et les quantités de béton.',
  crumbHome: 'Accueil',
  crumbResources: 'Ressources',
  h1: 'Ressources',
  intro:
    'Des guides pratiques, des listes de vérification de rénovation et des tableaux de référence rapide pour vous aider à planifier et réaliser votre projet.',
  calcH2: 'Calculateurs disponibles',
  calcSub: 'Accédez à un outil et obtenez votre liste de matériaux maintenant.',
  browseAll: 'Voir tous les calculateurs →',
  guidesH2: 'Guides étape par étape',
  guidesSub: 'Des guides détaillés liés aux calculateurs dont vous aurez besoin.',
  readGuide: 'Lire le guide →',
  comingSoon: 'À venir',
  refH2: 'Référence rapide',
  refSub: 'Les chiffres standards de l’industrie utilisés dans nos calculateurs.',
  ctaH2: 'D’autres guides à venir',
  ctaLead: 'Nous rédigeons des guides pratiques détaillés pour les projets de rénovation les plus courants.',
  ctaNote: 'Des questions ou des suggestions?',
  ctaGetInTouch: 'Écrivez-nous',
  guides: [
    {
      slug: 'how-to-paint-a-room',
      name: 'Comment peindre une pièce',
      blurb: 'Préparation, apprêt, découpe et rouleau — étape par étape, avec une liste complète de matériaux.',
      category: 'Peinture et murs',
      live: true,
    },
    {
      slug: 'bathroom-renovation-checklist',
      name: 'Liste pour rénover une salle de bain',
      blurb: 'Tout ce qu’il faut planifier, acheter et organiser pour une réfection complète de salle de bain.',
      category: 'Planification',
      live: true,
    },
    {
      slug: 'kitchen-remodel-planning-guide',
      name: 'Guide de planification de cuisine',
      blurb: 'L’ordre des travaux métier par métier, quoi prévoir, et où s’insèrent le revêtement de sol et les armoires.',
      category: 'Planification',
      live: true,
    },
    {
      slug: 'tile-installation-guide',
      name: 'Guide d’installation de carrelage',
      blurb: 'Préparation du support, lignes de pose, pose du carrelage, coulissage et scellement — pour planchers et murs.',
      category: 'Revêtement de sol et carrelage',
      live: true,
    },
    {
      slug: 'deck-building-materials-list',
      name: 'Liste de matériaux pour terrasse',
      blurb: 'Semelles, solives, planches de terrasse, quincaillerie — tout dans une seule liste.',
      category: 'Extérieur',
      live: true,
    },
    {
      slug: 'concrete-mixing-and-pouring',
      name: 'Mélanger et couler le béton',
      blurb: 'Ratios de mélange, temps de cure et erreurs courantes pour dalles, semelles et trous de poteau.',
      category: 'Béton',
      live: true,
    },
  ],
  calcResources: [
    { name: 'Calculateur de peinture', href: '/calculators/paint/', blurb: 'Gallons, apprêt, manchons et ruban pour toute pièce.' },
    { name: 'Calculateur de carrelage', href: '/calculators/tile/', blurb: 'Nombre de tuiles, mortier, coulis, croisillons et panneau d’appui.' },
    { name: 'Calculateur de béton', href: '/calculators/concrete/', blurb: 'Sacs ou verges pour dalles, semelles et trous de poteau.' },
    { name: 'Calculateur de paillis', href: '/calculators/mulch/', blurb: 'Sacs ou verges cubes en vrac pour toute profondeur de plate-bande.' },
  ],
  reference: [
    {
      title: 'Couverture de peinture selon le type de surface',
      items: [
        'Gypse lisse : 350–400 pi² par gallon',
        'Murs texturés ou rugueux : 250–300 pi² par gallon',
        'Bois nu (apprêt) : 200–300 pi² par gallon',
        'Maçonnerie : 100–150 pi² par gallon',
      ],
    },
    {
      title: 'Facteurs de perte de carrelage',
      items: [
        'Pose droite, pièce carrée : 10 %',
        'Pose en diagonale ou à 45° : 15–20 %',
        'Motif complexe ou nombreuses coupes : 20 % et plus',
        'Toujours acheter du même lot de teinture',
      ],
    },
    {
      title: 'Guides des joints de coulis',
      items: [
        '1/16 po : porcelaine rectifiée grand format',
        '1/8 po : pierre naturelle, carrelage poli',
        '3/16 po : céramique standard (le plus courant)',
        '1/4 po : carrelage fait main, rustique ou de brique',
      ],
    },
    {
      title: 'Rendement des sacs de béton',
      items: [
        'Sac de 40 lb : ~0,30 pi³',
        'Sac de 60 lb : ~0,45 pi³',
        'Sac de 80 lb : ~0,60 pi³',
        '1 verge = 27 pi³ ≈ 45 sacs de 80 lb',
      ],
    },
  ],
}

export default function Resources() {
  const locale = useLocale()
  const c = locale === 'fr' ? FR : EN
  const L = (en: string) => localizedHref(en, locale)

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <Seo
        title={c.seoTitle}
        description={c.seoDesc}
        path="/resources/"
      />
      <Breadcrumbs items={[{ name: c.crumbHome, href: L('/') }, { name: c.crumbResources, href: '/resources/' }]} />

      <h1 className="text-3xl">{c.h1}</h1>
      <p className="mt-2 text-text-muted max-w-2xl">
        {c.intro}
      </p>

      {/* Quick links to live calculators */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold text-text">{c.calcH2}</h2>
        <p className="mt-1 text-sm text-text-muted">{c.calcSub}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {c.calcResources.map((res) => (
            <Link
              key={res.href}
              to={L(res.href)}
              className="flex items-start gap-3 rounded-lg border border-border bg-surface p-4 hover:border-brand hover:bg-brand-tint/20 transition-colors"
            >
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-brand text-xs text-white">→</span>
              <div>
                <div className="font-medium text-text">{res.name}</div>
                <div className="text-sm text-text-muted">{res.blurb}</div>
              </div>
            </Link>
          ))}
        </div>
        <Link to={L('/calculators/')} className="mt-3 inline-block text-sm text-brand-text hover:underline">
          {c.browseAll}
        </Link>
      </section>

      {/* Guides */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-text">{c.guidesH2}</h2>
        <p className="mt-1 text-sm text-text-muted">{c.guidesSub}</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {c.guides.map((g) =>
            g.live ? (
              <Link
                key={g.slug}
                to={L(`/guides/${g.slug}/`)}
                className="rounded-lg border border-border bg-surface p-4 transition-colors hover:border-brand hover:bg-brand-tint/20"
              >
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-text">{g.category}</div>
                <div className="mt-1 font-medium text-text">{g.name}</div>
                <div className="mt-1 text-sm text-text-muted">{g.blurb}</div>
                <div className="mt-2 text-xs font-medium text-brand-text">{c.readGuide}</div>
              </Link>
            ) : (
              <div key={g.slug} className="rounded-lg border border-dashed border-border bg-surface-2 p-4 opacity-80">
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-text">{g.category}</div>
                <div className="mt-1 font-medium text-text-muted">{g.name}</div>
                <div className="mt-1 text-sm text-text-subtle">{g.blurb}</div>
                <div className="mt-2 text-xs text-text-subtle">{c.comingSoon}</div>
              </div>
            ),
          )}
        </div>
      </section>

      {/* Quick reference tables */}
      <section className="mt-12">
        <h2 className="text-xl font-semibold text-text">{c.refH2}</h2>
        <p className="mt-1 text-sm text-text-muted">{c.refSub}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {c.reference.map((r) => (
            <div key={r.title} className="rounded-xl border border-border bg-surface p-5">
              <h3 className="font-semibold text-text">{r.title}</h3>
              <ul className="mt-3 space-y-1">
                {r.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-text-muted">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter / updates placeholder */}
      <section className="mt-12 rounded-2xl border border-border bg-surface p-8 text-center">
        <h2 className="text-xl font-semibold text-text">{c.ctaH2}</h2>
        <p className="mt-2 text-text-muted">{c.ctaLead}</p>
        <p className="mt-2 text-sm text-text-subtle">
          {c.ctaNote} <a href="mailto:info@renosheets.com" className="text-brand-text underline">{c.ctaGetInTouch}</a>.
        </p>
      </section>
    </div>
  )
}
